import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  AuthUser,
  MemberRole,
  MemberRemovalPreview,
  MemberRemovalResult,
  PayRateRuleDto,
  PayRateUnit,
} from '@repo/shared-types';
import type {
  InviteMemberInput,
  UpdateMemberRoleInput,
  CreatePlaceholderMemberInput,
  ConvertMemberInput,
} from '@repo/validation';
import { fetchApi } from '../lib/api';
import i18n from '../i18n';

/** A member's teacher-payroll config, joined in by GET /org/members. `rate`
 *  is gone (THI-130) — the band table is `payRules`, empty when the teacher
 *  has no TeacherPaySetting row yet (never configured). */
export interface MemberPaySetting {
  rateUnit: PayRateUnit;
  baseSalary: number | null;
  payRules: PayRateRuleDto[];
}

interface MemberItem {
  id: string;
  role: MemberRole;
  joinedAt: string | null;
  removedAt: string | null;
  user: AuthUser;
  // Only GET /org/members joins this in; the placeholder-create and convert
  // routes return a MemberItem too but without it — treat as absent there.
  paySetting?: MemberPaySetting | null;
}

interface InvitationItem {
  id: string;
  email: string;
  role: MemberRole;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
}

// POST /org/invite returns either a brand-new invitation (201) or, when
// re-inviting a previously-removed teacher, a reactivation of their existing
// membership (200) — see org.ts.
type InviteResult =
  | { reactivated: true; membershipId: string }
  | { id: string; email: string; role: MemberRole; token: string; expiresAt: string };

/**
 * Active-only by default — the roster (TeamTab) and the teacher picker
 * (AssignDialog/SlotFormDialog) must never offer a removed teacher for new
 * assignment. Pass `includeRemoved: true` for views that resolve a removed
 * teacher's name against historical data (past sessions, timetable) — kept on
 * a distinct query key so its cache never collides with the active-only list.
 */
export function useMembers(options?: { includeRemoved?: boolean }) {
  const includeRemoved = options?.includeRemoved ?? false;
  return useQuery<MemberItem[]>({
    queryKey: includeRemoved ? ['org', 'members', { includeRemoved: true }] : ['org', 'members'],
    queryFn: () => fetchApi<MemberItem[]>(includeRemoved ? '/org/members?includeRemoved=1' : '/org/members'),
  });
}

export function useInvitations() {
  return useQuery<InvitationItem[]>({
    queryKey: ['org', 'invitations'],
    queryFn: () => fetchApi<InvitationItem[]>('/org/invitations'),
  });
}

export function useInviteMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: InviteMemberInput) =>
      fetchApi<InviteResult>('/org/invite', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (data) => {
      if ('reactivated' in data && data.reactivated) {
        // A removed teacher was re-invited: their membership is reactivated
        // immediately (no accept-invite step) and it retakes an org seat, so
        // both the roster and the subscription's used/max seat count must
        // refresh right away — not just the (irrelevant here) invitations list.
        queryClient.invalidateQueries({ queryKey: ['org', 'members'] });
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
        toast.success(i18n.t('team.toastReactivated', { ns: 'settings' }));
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['org', 'invitations'] });
      toast.success(i18n.t('team.toastInvited', { ns: 'settings' }));
    },
    onError: (err: Error & { code?: string }) => {
      if (err.code === 'ALREADY_MEMBER') {
        toast.error(i18n.t('team.toastAlreadyMember', { ns: 'settings' }));
      } else {
        toast.error(err.message || i18n.t('team.toastInviteError', { ns: 'settings' }));
      }
    },
  });
}

// THI-175 (ruling 4): revoking a still-pending invitation frees the seat it
// holds — the org no longer has to wait out the 7-day expiry to reclaim it.
export function useRevokeInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<{ deleted: true }>(`/org/invitations/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'invitations'] });
      // Revoking frees a seat — the subscription's used/max count must
      // refresh too, same as removing a member.
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      toast.success(i18n.t('team.toastInvitationRevoked', { ns: 'settings' }));
    },
    onError: (err: Error & { code?: string }) => {
      if (err.code === 'ALREADY_ACCEPTED') {
        toast.error(i18n.t('team.toastInvitationAlreadyAccepted', { ns: 'settings' }));
        queryClient.invalidateQueries({ queryKey: ['org', 'invitations'] });
      } else {
        toast.error(err.message || i18n.t('team.toastInvitationRevokeError', { ns: 'settings' }));
      }
    },
  });
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateMemberRoleInput & { id: string }) =>
      fetchApi<{ message: string }>(`/org/members/${id}/role`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'members'] });
      toast.success(i18n.t('team.toastRoleUpdated', { ns: 'settings' }));
    },
    onError: (err: Error) => {
      toast.error(err.message || i18n.t('team.toastRoleError', { ns: 'settings' }));
    },
  });
}

/** Fetches the impact-preview counts for a soft-removal, on demand (not
 * cached) — driven from the TeamTab confirmation dialog right before the
 * owner/admin commits to `useRemoveMember`. */
export function useMemberRemovalPreview() {
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<MemberRemovalPreview>(`/org/members/${id}/removal-preview`),
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; name: string }) =>
      fetchApi<MemberRemovalResult>(`/org/members/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['org', 'members'] });
      // Removing a teacher frees an org seat — refresh the subscription's
      // used/max count too.
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      toast.success(
        i18n.t('team.toastRemoved', {
          ns: 'settings',
          name: variables.name,
          archivedSlots: data.archivedSlots,
          removedSessions: data.removedSessions,
        }),
      );
    },
    onError: (err: Error) => {
      toast.error(err.message || i18n.t('team.toastRemoveError', { ns: 'settings' }));
    },
  });
}

export function useCreatePlaceholderMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreatePlaceholderMemberInput) =>
      fetchApi<MemberItem>('/org/members/placeholder', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'members'] });
      toast.success(i18n.t('team.toastPlaceholderAdded', { ns: 'settings' }));
    },
    onError: (err: Error) => {
      toast.error(err.message || i18n.t('team.toastPlaceholderError', { ns: 'settings' }));
    },
  });
}

export function useConvertMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: ConvertMemberInput & { id: string }) =>
      fetchApi<MemberItem>(`/org/members/${id}/convert`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'members'] });
      toast.success(i18n.t('team.toastConverted', { ns: 'settings' }));
    },
    onError: (err: Error & { code?: string }) => {
      if (err.code === 'EMAIL_EXISTS') {
        toast.error(i18n.t('team.toastConvertEmailExists', { ns: 'settings' }));
      } else {
        toast.error(err.message || i18n.t('team.toastConvertError', { ns: 'settings' }));
      }
    },
  });
}
